import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { User, Mail, Lock, CheckCircle2, AlertCircle, Loader2, IdCard, KeyRound, Eye, EyeOff, Shield, Info } from "lucide-react";
import { backendApi } from "./api/backendApi";

// Self-service Settings page, lets the logged-in user change their name,
// email, and/or password. Backed by PUT /api/auth/me.
const T = {
  green: '#1A3C2B', greenDeep: '#0E2118', greenMid: '#255238', greenTint: '#EDF3EF', greenAccent: '#2E7D52',
  bg: '#F4F5F0', card: '#FFFFFF', line: '#E3E2DC', lineSoft: '#EEEEE9',
  ink: '#111418', body: '#3A4149', muted: '#687079', faint: '#9BA3AB',
};

function PasswordStrength({ password }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const colors = ['#E3E2DC', '#C0413D', '#B9822E', '#3B6FA6', '#2F7D4F'];
  const labels = ['', 'Weak', 'Fair', 'Good', 'Strong'];

  if (!password) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 5 }}>
        {[1,2,3,4].map(i => (
          <div key={i} style={{ flex:1, height:3, borderRadius:99, background: i <= score ? colors[score] : T.lineSoft, transition:'background 0.3s' }} />
        ))}
      </div>
      <p style={{ fontSize: 11, color: colors[score] || T.faint, margin:0, fontWeight: 600 }}>{labels[score]}</p>
    </div>
  );
}

function InputField({ icon: Icon, type = "text", value, onChange, placeholder, autoComplete, label, hint }) {
  const [focused, setFocused] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const isPwd = type === 'password';
  const inputType = isPwd ? (showPwd ? 'text' : 'password') : type;

  return (
    <div>
      {label && <p style={{ fontSize: 12, fontWeight: 600, color: T.body, margin: '0 0 6px' }}>{label}</p>}
      <div style={{ position: 'relative' }}>
        <Icon size={15} color={focused ? T.green : T.faint} style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', pointerEvents:'none', transition:'color 0.15s' }} strokeWidth={2} />
        <input
          type={inputType}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          autoComplete={autoComplete}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: '100%', paddingLeft: 38, paddingRight: isPwd ? 40 : 12,
            paddingTop: 10, paddingBottom: 10,
            border: `1.5px solid ${focused ? T.green : T.line}`,
            borderRadius: 10, fontSize: 13.5, color: T.ink,
            background: focused ? T.card : T.bg, outline: 'none',
            boxSizing: 'border-box',
            boxShadow: focused ? `0 0 0 3px ${T.greenTint}` : 'none',
            transition: 'all 0.15s',
          }}
        />
        {isPwd && (
          <button type="button" onClick={() => setShowPwd(!showPwd)}
            style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:T.faint, padding:2 }}>
            {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        )}
      </div>
      {hint && <p style={{ fontSize:11, color:T.faint, margin:'4px 0 0' }}>{hint}</p>}
    </div>
  );
}

export default function Settings() {
  const queryClient = useQueryClient();
  const storedUser = backendApi.auth.getStoredUser();

  const [fullName, setFullName] = useState(storedUser?.full_name || storedUser?.name || "");
  const [email, setEmail] = useState(storedUser?.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [feedback, setFeedback] = useState(null);

  const updateMutation = useMutation({
    mutationFn: (payload) => backendApi.auth.updateProfile(payload),
    onSuccess: () => {
      setFeedback({ type: "success", message: "Profile updated successfully." });
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
    },
    onError: (err) => setFeedback({ type: "error", message: err.message || "Failed to update." }),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setFeedback(null);
    if (!fullName.trim()) return setFeedback({ type:"error", message:"Full name cannot be empty." });
    if (!email.trim()) return setFeedback({ type:"error", message:"Email cannot be empty." });
    const isChangingPwd = newPassword.length > 0;
    if (isChangingPwd) {
      if (!currentPassword) return setFeedback({ type:"error", message:"Enter your current password." });
      if (newPassword.length < 6) return setFeedback({ type:"error", message:"New password must be at least 6 characters." });
      if (newPassword !== confirmPassword) return setFeedback({ type:"error", message:"Passwords do not match." });
    }
    updateMutation.mutate({ full_name: fullName.trim(), email: email.trim(), current_password: isChangingPwd ? currentPassword : undefined, new_password: isChangingPwd ? newPassword : undefined });
  };

  const initial = (storedUser?.full_name || storedUser?.name || 'U')[0].toUpperCase();

  return (
    <div style={{ background: T.bg, minHeight: "100%" }}>
      {/* Hero cover — same structure as Profile for visual consistency */}
      <div style={{ background: `linear-gradient(135deg, ${T.greenDeep} 0%, ${T.greenMid} 55%, ${T.greenAccent} 100%)`, position:'relative', overflow:'hidden' }}>
        {[{ top:-80,right:-80,size:320 },{ bottom:-60,left:'25%',size:220 }].map((s,i) => (
          <div key={i} style={{ position:'absolute', width:s.size, height:s.size, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none', top:s.top, bottom:s.bottom, left:s.left, right:s.right }} />
        ))}
        <div style={{ padding:'44px 36px 40px', position:'relative', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:20 }}>
            <div style={{
              width:72, height:72, borderRadius:18, flexShrink:0,
              background:'rgba(255,255,255,0.12)', border:'2px solid rgba(255,255,255,0.22)',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:28, fontWeight:800, color:'#fff',
            }}>{initial}</div>
            <div>
              <h1 style={{ fontSize:22, fontWeight:800, color:'#fff', margin:0, letterSpacing:'-0.02em' }}>Account Settings</h1>
              <p style={{ fontSize:13, color:'rgba(255,255,255,0.6)', margin:'4px 0 0' }}>Update your name, email, and security preferences</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding:'28px 36px 48px' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) 300px', gap:20, alignItems:'start' }}>

            {/* Left: form */}
            <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

              {/* Feedback banner */}
              {feedback && (
                <div style={{
                  display:'flex', alignItems:'center', gap:10, padding:'12px 16px', borderRadius:12,
                  border:`1.5px solid ${feedback.type==='success'?'#BBF7D0':'#FECACA'}`,
                  background:feedback.type==='success'?'#F0FDF4':'#FEF2F2',
                  fontSize:13, fontWeight:500, color:feedback.type==='success'?'#15803D':'#DC2626',
                }}>
                  {feedback.type==='success' ? <CheckCircle2 size={16} strokeWidth={2.2} /> : <AlertCircle size={16} strokeWidth={2.2} />}
                  {feedback.message}
                </div>
              )}

              {/* Profile Details */}
              <div style={{ background:T.card, border:`1.5px solid ${T.line}`, borderRadius:20, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ padding:'16px 20px', borderBottom:`1.5px solid ${T.lineSoft}`, display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:34, height:34, borderRadius:9, background:T.greenTint, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <IdCard size={16} color={T.green} strokeWidth={2} />
                  </div>
                  <div>
                    <p style={{ fontSize:14.5, fontWeight:700, color:T.ink, margin:0 }}>Profile Details</p>
                    <p style={{ fontSize:11.5, color:T.muted, margin:'2px 0 0' }}>Your public-facing identity in the HRMS</p>
                  </div>
                </div>
                <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:16 }}>
                  <InputField icon={User} label="Full Name" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name" />
                  <InputField icon={Mail} type="email" label="Email Address" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" hint="Used for login and system notifications" />
                </div>
              </div>

              {/* Change Password */}
              <div style={{ background:T.card, border:`1.5px solid ${T.line}`, borderRadius:20, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ padding:'16px 20px', borderBottom:`1.5px solid ${T.lineSoft}`, display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ width:34, height:34, borderRadius:9, background:T.greenTint, display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <KeyRound size={16} color={T.green} strokeWidth={2} />
                  </div>
                  <div>
                    <p style={{ fontSize:14.5, fontWeight:700, color:T.ink, margin:0 }}>Change Password</p>
                    <p style={{ fontSize:11.5, color:T.muted, margin:'2px 0 0' }}>Leave blank to keep your current password</p>
                  </div>
                </div>
                <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:16 }}>
                  <InputField icon={Lock} type="password" label="Current Password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" autoComplete="current-password" />
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
                    <div>
                      <InputField icon={Lock} type="password" label="New Password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 characters" autoComplete="new-password" />
                      <PasswordStrength password={newPassword} />
                    </div>
                    <InputField icon={Lock} type="password" label="Confirm Password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" />
                  </div>
                </div>
              </div>

              {/* Save */}
              <div style={{ display:'flex', justifyContent:'flex-end' }}>
                <button type="submit" disabled={updateMutation.isLoading}
                  style={{
                    display:'inline-flex', alignItems:'center', gap:8, padding:'11px 28px',
                    borderRadius:12, background: updateMutation.isLoading ? T.greenMid : T.green,
                    color:'#fff', fontSize:14, fontWeight:700, border:'none',
                    cursor:updateMutation.isLoading?'not-allowed':'pointer',
                    opacity:updateMutation.isLoading?0.7:1, transition:'all 0.15s',
                    boxShadow:'0 2px 12px rgba(26,60,43,0.2)',
                  }}>
                  {updateMutation.isLoading && <Loader2 size={15} className="animate-spin" />}
                  Save Changes
                </button>
              </div>
            </div>

            {/* Right: sidebar tips */}
            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {/* Tips */}
              <div style={{ background:T.card, border:`1.5px solid ${T.line}`, borderRadius:20, padding:'20px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16 }}>
                  <Info size={15} color={T.green} />
                  <p style={{ fontSize:13.5, fontWeight:700, color:T.ink, margin:0 }}>What you can update</p>
                </div>
                {[
                  { icon:User, label:'Display name', desc:'How your name appears across HRMS.' },
                  { icon:Mail, label:'Email address', desc:'Used for login and notifications.' },
                  { icon:Lock, label:'Password', desc:'Leave blank if you want to skip.' },
                ].map(({ icon:Icon, label, desc }) => (
                  <div key={label} style={{ display:'flex', gap:12, marginBottom:14 }}>
                    <div style={{ width:32, height:32, borderRadius:9, background:T.greenTint, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <Icon size={14} color={T.green} strokeWidth={2} />
                    </div>
                    <div>
                      <p style={{ fontSize:12.5, fontWeight:700, color:T.ink, margin:0 }}>{label}</p>
                      <p style={{ fontSize:11.5, color:T.muted, margin:'2px 0 0', lineHeight:1.4 }}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Security tip */}
              <div style={{ background:`linear-gradient(135deg, ${T.greenDeep} 0%, ${T.greenMid} 100%)`, borderRadius:20, padding:'20px 22px', position:'relative', overflow:'hidden' }}>
                <div style={{ position:'absolute', top:-30, right:-30, width:100, height:100, borderRadius:'50%', background:'rgba(255,255,255,0.05)' }} />
                <div style={{ position:'relative' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                    <Shield size={16} color='rgba(255,255,255,0.8)' />
                    <p style={{ fontSize:13, fontWeight:700, color:'#fff', margin:0 }}>Security tip</p>
                  </div>
                  <p style={{ fontSize:12, color:'rgba(255,255,255,0.6)', margin:0, lineHeight:1.55 }}>
                    Use a strong password — at least 8 characters mixing uppercase, numbers, and symbols.
                  </p>
                  <div style={{ marginTop:14, padding:'10px 12px', background:'rgba(255,255,255,0.08)', borderRadius:10 }}>
                    {['One uppercase letter','One number','One special character','8+ characters'].map(tip => (
                      <div key={tip} style={{ display:'flex', alignItems:'center', gap:7, marginBottom:5 }}>
                        <CheckCircle2 size={12} color='rgba(74,222,128,0.7)' />
                        <span style={{ fontSize:11.5, color:'rgba(255,255,255,0.6)' }}>{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}